// 계측 프로브 — 이 스텝이 재려는 셋을 한 화면에 올린다
//
// 화면다운 화면이 아니다. 이 페이지가 있는 이유는 셋이고, 셋 다 **늦게 알면 비싼 것**
// 이라 가장 이른 자리에서 재기 시작한다.
//
// 하나 — **배포 환경이 실제로 서는가.** 도메인 없는 IP 직결 HTTP 에서 페이지가 열리는가.
// 둘 — **시계 동기가 도는가.** 서버 시각과 내 시각의 차이를 밀리초로 재어 상시 표시한다.
//   이 제품의 표시 시각 편차 예산이 p95 200밀리초이고, 여기 뜨는 숫자가 곧 그 예산을
//   지키는지 여부다.
// 셋 — **오디오가 계약대로 우는가.** 이것이 두 갈래다. 소리가 실제로 귀에 들리는지와
//   반복해 들어 괴롭지 않은지는 **사람만** 안다 — iOS 무음 스위치와 자동재생 정책이
//   도메인 없는 HTTP 에서 어떻게 되는지도 실기기로만 알 수 있고, 시험음 버튼이 그
//   확인을 연다. 나머지 절반은 기계가 판정한다: 이 기기의 오디오 스택이 렌더한 파형이
//   성질 셋을 만족하는가. 자가 판정 버튼이 그것이고, 판정 대상은 **화면이 실제로 부르는
//   예약 코드가 렌더한 버퍼**다.
//
// 여기 있는 코드는 버려지지 않는다. 시계 동기 모듈이 그대로 상단 고정줄의 ±밀리초가
// 되고, 오디오 모듈이 그대로 열람 화면의 신호 존이 된다.

import { useCallback, useEffect, useRef, useState } from 'react'

import { toMillis } from '../../../core/time/brand.ts'
import { systemClock, type Clock } from '../../../core/time/clock.ts'
import { SIGNAL_PROFILE, signalWindowSec } from '../../audio/profile.ts'
import { renderSignalOffline } from '../../audio/render.ts'
import { armAudio, scheduleSignal, type AudioContextLike, type SignalResult } from '../../audio/signal.ts'
import { judgeSignal, type SignalVerdict } from '../../audio/verdict.ts'
import { ClockSync, httpTransport } from '../../clock/sync.ts'
import { TopBar } from '../../shell/topbar.tsx'
import { useLang } from '../../shell/use-lang.ts'
import { blockedReasonText } from '../../audio/reason-text.ts'

/**
 * 표본을 아직 다 못 모았을 때의 간격. 처음 붙을 때와 시계가 튀어 다시 모을 때 둘 다
 * 이 간격을 쓴다. 표본 하나로는 비대칭을 걸러 낼 수 없어 셋을 모아야 하는데, 그 일에
 * 5초 폴링을 쓰면 14초가 걸린다.
 */
const WARMUP_GAP_MS = 150
/** 그 뒤로는 이 간격으로 계속 잰다 — 편차는 한 번 재고 끝나는 값이 아니다. */
const STEADY_GAP_MS = 5_000

/** 버튼을 누른 뒤 첫 표지까지의 여백. 누르는 손이 화면에서 떨어질 시간이다. */
const LIVE_LEAD_SEC = 0.4

export interface ProbeScreenProps {
  clock?: Clock
  sync?: ClockSync
}

function createAudioContext(): AudioContextLike | null {
  const w = window as unknown as {
    AudioContext?: new () => unknown
    webkitAudioContext?: new () => unknown
  }
  const Ctor = w.AudioContext ?? w.webkitAudioContext
  if (Ctor === undefined) return null
  // 브라우저 타입을 그대로 쓰지 않고 이 모듈이 정한 최소 인터페이스로 좁힌다. 그래야
  // 단위 테스트가 가짜 컨텍스트로 **예약된 시각 자체**를 잴 수 있다.
  return new Ctor() as AudioContextLike
}

// 시각과 편차의 **표시 형식은 고정줄이 소유한다.** 여기서 한 번 더 만들면 두 자리가
// 서로 다른 규칙으로 같은 값을 그리게 되고, 그 차이를 사람이 오차로 읽는다.

export function ProbeScreen(props: ProbeScreenProps = {}): React.ReactElement {
  const [clock] = useState<Clock>(() => props.clock ?? systemClock())
  const [sync] = useState<ClockSync>(
    () => props.sync ?? new ClockSync({ clock, transport: httpTransport() }),
  )

  // 화면을 다시 그리게 하는 심장. 값을 상태에 쌓지 않고 매 프레임 시계에서 다시 읽는
  // 이유는, 쌓아 두면 그 값이 언제 찍힌 것인지가 화면에서 사라지기 때문이다.
  const [, forceRender] = useState(0)
  const [syncError, setSyncError] = useState<string>('')
  const [resyncNote, setResyncNote] = useState<string>('')
  const [signalStatus, setSignalStatus] = useState<string>('아직 울리지 않았다')
  const [signalOk, setSignalOk] = useState<boolean | null>(null)
  const [audioArmed, setAudioArmed] = useState(false)
  const [verdict, setVerdict] = useState<SignalVerdict | null>(null)
  const [verdictError, setVerdictError] = useState<string>('')
  // 언어 전환은 고정줄 우측 끝 한 자리에만 산다. 이 화면은 그 자리를 세우는 것까지만
  // 하고, 문자열을 실제로 영어로 바꾸는 것은 용어표가 서는 마지막 스텝이 한다.
  // 저장소에 든 하나를 본다. 이 화면은 입장 없이 열리므로 언어를 세션에 둘 수 없다.
  const { lang, choose: setLang } = useLang()
  const audioRef = useRef<AudioContextLike | null>(null)
  // 직전 예약을 들고 있는다. 다시 누르면 그것부터 취소한다 — 취소 경로를 여기서
  // 쓰지 않으면 열람 화면이 신호 대상을 바꿀 때 처음 쓰게 되고, 그때 처음 쓰는 경로는
  // 실전에서 처음 실패한다.
  const scheduledRef = useRef<SignalResult | null>(null)

  const probeOnce = useCallback(async (): Promise<boolean> => {
    try {
      await sync.probe()
      setSyncError('')
      // **다시 수렴을 마치면 「다시 수렴 중」은 참이 아니다.** 세워만 두고 지우는 자리가
      // 없어서, 한 번 잔 기기는 그 뒤로 몇 시간이 지나도 이 문장을 달고 있었다. 방금 닫힌
      // T7 개정이 다시 잡기 바에서 걷어낸 얼어붙은 문장과 같은 부류다.
      if (sync.estimate !== null) setResyncNote('')
      return true
    } catch (error) {
      setSyncError(String(error))
      return false
    }
  }, [sync])

  useEffect(() => {
    let alive = true
    let timer: ReturnType<typeof setTimeout> | undefined

    // **간격을 상태에서 고른다.** 셸 훅과 같은 규칙이다. 시계가 뛰어 창이 비면 5초 폴링으로
    // 표본 셋을 다시 모으는 데 14초가 걸리는데, 그 14초 동안 밴드의 편차 칸도 이 화면의
    // 상세 칸도 함께 빈다. 자물쇠는 「직전 왕복이 성공했는가」이고, 그래서 빠른 경로는 성공한
    // 왕복만 타 재수렴 한 번에 저절로 묶인다. 화면마다 규칙이 갈리면 반드시 한 곳이 뒤처지고,
    // 이 화면이 바로 그 「한 곳」이었다.
    const loop = async (): Promise<void> => {
      const ok = await probeOnce()
      if (!alive) return
      const gap = ok && sync.heldSamples < sync.minSamples ? WARMUP_GAP_MS : STEADY_GAP_MS
      timer = setTimeout(() => void loop(), gap)
    }
    void loop()

    return () => {
      alive = false
      if (timer !== undefined) clearTimeout(timer)
    }
  }, [probeOnce, sync])

  // 단조 시계 불연속 감지. 화면 꺼짐 방지를 못 쓰는 것이 확정 사실이라 폰이 자는 것은
  // 예외가 아니라 상시 조건이고, 자고 일어난 뒤의 낡은 편차는 **틀린 값을 자신 있게**
  // 표시한다. 그래서 복귀 시점에 두 축을 교차 검사해 어긋났으면 처음부터 다시 수렴한다.
  useEffect(() => {
    const onVisible = (): void => {
      if (document.visibilityState !== 'visible') return
      const check = sync.checkContinuity()
      if (!check.discontinuous) return
      const seconds = Math.round(toMillis(check.gapMs) / 1000)
      setResyncNote(`화면이 돌아오는 동안 기기가 약 ${String(seconds)}초 잤다 — 다시 수렴 중`)
      void probeOnce()
    }
    document.addEventListener('visibilitychange', onVisible)
    return () => {
      document.removeEventListener('visibilitychange', onVisible)
    }
  }, [sync, probeOnce])

  // 매 프레임 다시 그린다. requestAnimationFrame 을 쓰는 이유는 브라우저가 이것을
  // 배경 탭에서 **정직하게 늦추기** 때문이다 — 배경에서 화면이 멈추는 것은 버그가
  // 아니라 관측해야 할 사실이고, setInterval 로 감추면 그 사실이 안 보인다.
  useEffect(() => {
    let handle = 0
    const frame = (): void => {
      forceRender((n) => (n + 1) % 1_000_000)
      handle = requestAnimationFrame(frame)
    }
    handle = requestAnimationFrame(frame)
    return () => {
      cancelAnimationFrame(handle)
    }
  }, [])

  const onTestSignal = useCallback(async (): Promise<void> => {
    audioRef.current ??= createAudioContext()
    const ctx = audioRef.current
    if (ctx === null) {
      setSignalOk(false)
      setSignalStatus('이 브라우저에 Web Audio 가 없다. 소리를 낼 방법이 없다.')
      return
    }

    // 반드시 사람의 제스처 **안에서** 깨운다. 밖에서 부르면 브라우저가 풀어 주지 않는다.
    const arm = await armAudio(ctx)
    setAudioArmed(arm.armed)
    if (!arm.armed) {
      setSignalOk(false)
      setSignalStatus(arm.reason)
      return
    }

    // 겹침은 판정 계약의 불통 사유다. 다시 누르면 옛 예약부터 죽인다.
    const previous = scheduledRef.current
    if (previous !== null && previous.kind === 'scheduled') previous.cancel()

    const startAtSec = ctx.currentTime + signalWindowSec(SIGNAL_PROFILE) + LIVE_LEAD_SEC
    const result = scheduleSignal(ctx, { startAtSec })
    scheduledRef.current = result

    if (result.kind === 'scheduled') {
      setSignalOk(true)
      setSignalStatus(
        `${signalWindowSec(SIGNAL_PROFILE).toFixed(1)}초 구간을 예약했다. ` +
          '1초 간격 표지를 세다가, **여섯째 소리가 시작되는 순간**이 개시다 — ' +
          '박자만으로 그 순간을 미리 짚을 수 있어야 한다.',
      )
    } else if (result.kind === 'too-late') {
      setSignalOk(false)
      setSignalStatus(`창이 이미 지났다 (${result.lateBySec.toFixed(3)}초 늦음). 울리지 않는다.`)
    } else {
      setSignalOk(false)
      setSignalStatus(result.reason)
    }
  }, [])

  // 이 기기의 오디오 스택이 렌더한 파형을 기계가 판정한다. **소리를 내지 않는다** —
  // 오프라인 렌더라 스피커를 거치지 않고, 그래서 무음 스위치나 활성화 제스처와 무관하게
  // 돈다. 사람이 듣는 것과 기계가 재는 것을 가른 자리가 여기다.
  const onJudge = useCallback(async (): Promise<void> => {
    setVerdictError('')
    try {
      const rendered = await renderSignalOffline()
      setVerdict(
        judgeSignal({
          samples: rendered.samples,
          sampleRate: rendered.sampleRate,
          startAtSec: rendered.startAtSec,
        }),
      )
    } catch (error) {
      setVerdict(null)
      setVerdictError(String(error))
    }
  }, [])

  const estimate = sync.estimate
  const serverNow = sync.serverNow()
  const shownMillis = toMillis(serverNow ?? clock.now())
  const lag = sync.lastEventLoopLagMs

  return (
    <>
      {/* 시각과 편차는 **고정줄이 소유한다.** 화면 본문에 같은 값을 한 번 더 그리면
          두 자리가 서로 다른 프레임에서 갱신되어, 사람이 그 차이를 편차로 읽는다. */}
      <TopBar
        utcMillis={shownMillis}
        // **규칙을 여기서 다시 쓰지 않는다.** 이 화면만 훅 밖에서 동기화기를 직접 드는데,
        // 그래서 전에는 「null 이면 측정 중」이라는 규칙의 사본이 여기 하나 더 있었다.
        // 사본은 반드시 뒤처진다. 진행 표시를 조건 없이 넘기고 있어서 값이 살아난 뒤에도
        // 곁말을 영영 달 참이었고, 그것을 타입도 단위 검사도 못 잡았다.
        skew={sync.skewView()}
        lang={lang}
        onLangChange={setLang}
      />

      <main className="mx-auto max-w-2xl px-4 py-block font-sans">
        <h1 className="text-lg font-semibold">rally ops room — 계측 프로브</h1>
        <p className="mt-line text-sm text-ink-dim">
          이 페이지는 셋을 잰다: 이 주소가 열리는가 · 시계가 맞는가 · 소리가 우는가.
        </p>

        <section className="mt-section rounded border px-4 py-block" aria-label="시계">
          <div className="text-xs uppercase text-ink-mute">시계 동기 상세</div>

          <dl className="mt-group grid grid-cols-2 gap-x-4 gap-y-line font-mono text-sm">
            <dt className="text-ink-mute">왕복</dt>
            <dd data-testid="clock-rtt">
              {estimate === null ? '—' : `${String(toMillis(estimate.roundTripMs))} ms`}
            </dd>
            <dt className="text-ink-mute">표본 (채택/보유)</dt>
            <dd data-testid="clock-samples">
              {estimate === null
                ? `0/${String(sync.heldSamples)}`
                : `${String(estimate.usedCount)}/${String(estimate.observedCount)}`}
            </dd>
            <dt className="text-ink-mute">서버 이벤트 루프 지연</dt>
            <dd data-testid="server-loop-lag">{lag === null ? '—' : `${String(lag)} ms`}</dd>
          </dl>

          {syncError !== '' && (
            <p className="mt-group text-sm" data-testid="sync-error">
              시계 동기 실패: {syncError}
            </p>
          )}
          {resyncNote !== '' && (
            <p className="mt-group text-sm" data-testid="resync-note">
              {resyncNote}
            </p>
          )}
        </section>

        <section className="mt-block rounded border px-4 py-block" aria-label="signal 시험음">
          <div className="text-xs uppercase text-ink-mute">signal 시험음, 사람이 듣는다</div>
          <button
            type="button"
            className="mt-group rounded border px-4 py-control text-base"
            data-testid="signal-button"
            onClick={() => {
              void onTestSignal()
            }}
          >
            {signalWindowSec(SIGNAL_PROFILE).toFixed(0)}초 구간을 듣는다
          </button>
          <p className="mt-group text-sm" data-testid="signal-status" data-signal-ok={String(signalOk)}>
            {blockedReasonText(signalStatus, lang)}
          </p>
          <p className="mt-line text-xs text-ink-mute" data-testid="audio-armed">
            오디오 무장: {audioArmed ? '됨' : '아직'}
          </p>
          <p className="mt-group text-xs text-ink-mute">
            듣고 답할 것이 셋이다 — 표지의 박자만으로 개시 순간을 미리 짚을 수 있는가,
            「언제가 개시였나」를 물으면 한 곳만 가리키는가, 그리고 이 소리를 매 dispatch 마다
            반복해 들을 수 있는가. 셋째가 「아니오」면 고치는 것은 계약이 아니라 프로파일이다.
          </p>
          <p className="mt-group text-xs text-ink-mute">
            개시 뒤에도 남는 출발음의 꼬리가 착지 확인으로 도움인지 주의 분산으로 방해인지도
            함께 들을 것 — 이 자리에는 아직 아무 근거가 없다. 소리가 안 나면 그 이유가 위에
            적힌다. iPhone 은 측면 무음 스위치를 먼저 확인할 것.
          </p>
        </section>

        <section className="mt-block rounded border px-4 py-block" aria-label="signal 자가 판정">
          <div className="text-xs uppercase text-ink-mute">signal 자가 판정, 기계가 잰다</div>
          <button
            type="button"
            className="mt-group rounded border px-4 py-control text-base"
            data-testid="signal-judge-button"
            onClick={() => {
              void onJudge()
            }}
          >
            이 기기가 렌더한 파형을 판정한다
          </button>

          {verdictError !== '' && (
            <p className="mt-group text-sm" data-testid="signal-verdict-error">
              {verdictError}
            </p>
          )}

          {verdict !== null && (
            <div className="mt-group" data-testid="signal-verdict" data-verdict-ok={String(verdict.ok)}>
              <p className="text-sm font-semibold">
                {verdict.ok ? '판정 통과' : '판정 불통'} — 항목 {String(verdict.checks.length)}개
              </p>
              <ul className="mt-group space-y-line font-mono text-xs">
                {verdict.checks.map((check) => (
                  <li key={check.id} data-testid={`signal-check-${check.id}`} data-ok={String(check.ok)}>
                    <span className="mr-2">{check.ok ? '초록' : '빨강'}</span>
                    {check.label} — {check.detail}
                  </li>
                ))}
              </ul>
            </div>
          )}

          <p className="mt-group text-xs text-ink-mute">
            이 판정은 <strong>필요조건일 뿐이다.</strong> 일곱 항목이 전부 초록이어도 사람이
            괴롭다고 하면 불통이고, 그때 고치는 것은 성질이 아니라 프로파일이다. 거슬림은
            스펙트럼 분포와 거칠기가 정하는 것이라 단일 음압 수치로는 재지지 않는다.
          </p>
        </section>
      </main>
    </>
  )
}
